import { useState } from 'react';
import { useTranslate } from 'react-admin';
import { Alert, Button, Stack, Typography } from '@mui/material';

import {
  approveLaneResume,
  pauseLane,
  proposeLaneResume,
  type LanePauseScope,
  type LanePauseState,
} from 'src/pages/admin/services/vaultOperationsService';
import { PermissionButton } from 'src/routes/components/permission-button';

import { ConfirmDialog, type ConfirmSpec } from './ConfirmDialog';
import { OPERATIONS_NS as NS, OUTCOME_BUTTON_SX, SIGNERS_PERMISSION_RESOURCE } from './constants';
import { formatInstant, middle, requiredText } from './helpers';
import { TimeText } from './parts';
import type { Queue } from './useQueue';

const LANE_SCOPES: LanePauseScope[] = ['USER_OUTFLOW', 'COMPANY_INTERNAL'];

interface Props {
  queue: Queue<LanePauseState[]>;
  onSettled: () => Promise<unknown> | void;
}

/**
 * XBIT-4993: the treasury lane pauses, above the tabs because a pause holds orders listed under Payouts, Transfers and Waiting
 * for signers alike. The BFF serves no reason and no pauser, so a row says which scope is stopped and since when, and, while a
 * resume is half-way, who proposed it. A propose never lifts the pause: only the second operator's approval does.
 */
export const LanePauseBanner = ({ queue, onSettled }: Props) => {
  const translate = useTranslate();
  const t = (key: string, options?: object) => translate(`${NS}.lanePause.${key}`, options);
  const [spec, setSpec] = useState<ConfirmSpec | null>(null);
  // The pause control outlives a failed read. The incident that breaks the lane-pause read is the one the
  // pause exists for, and an operator who cannot read the list must still be able to stop the money.
  const states = queue.data ?? [];

  const scopeName = (scope: LanePauseScope) => t(`scope.${scope}`);
  const paused = states.filter((s) => s.paused);
  const sinceOf = (scope: LanePauseScope) => {
    // no read is not the same claim as no history: saying "never paused" here would invent an answer
    if (!queue.data) return t('unknownHistory');
    const at = states.find((s) => s.scope === scope)?.at;
    return at ? t('lastChanged', { time: formatInstant(at) }) : t('never');
  };

  const pause = () =>
    setSpec({
      title: t('pauseTitle'),
      moneyMoves: false,
      money: t('pauseNoMoney'),
      approver: t('pauseApprover'),
      ifNobody: t('pauseIfNobody'),
      confirmLabel: t('pauseConfirm'),
      confirmColor: 'error',
      fields: [
        {
          name: 'scope',
          label: t('pauseScope'),
          initial: LANE_SCOPES[0],
          options: LANE_SCOPES.map((scope) => ({
            value: scope,
            label: t('scopeOption', { scope: scopeName(scope), history: sinceOf(scope) }),
          })),
          validate: () => undefined,
        },
        {
          name: 'reason',
          label: t('reason'),
          hint: t('pauseReasonHint'),
          multiline: true,
          validate: requiredText,
        },
      ],
      submit: (v) => pauseLane(v.scope as LanePauseScope, v.reason),
      doneMessage: t('done.pause'),
    });

  const propose = (scope: LanePauseScope) =>
    setSpec({
      title: t('proposeTitle', { scope: scopeName(scope) }),
      moneyMoves: false,
      money: t('proposeNoMoney'),
      approver: t('proposeApprover'),
      ifNobody: t('proposeIfNobody'),
      confirmLabel: t('proposeResume'),
      fields: [
        {
          name: 'reason',
          label: t('reason'),
          hint: t('proposeReasonHint'),
          multiline: true,
          validate: requiredText,
        },
      ],
      submit: (v) => proposeLaneResume(scope, v.reason),
      doneMessage: t('done.propose'),
      outcome: t('proposeOutcome', { scope: scopeName(scope) }),
    });

  const approve = (scope: LanePauseScope) =>
    setSpec({
      title: t('approveTitle', { scope: scopeName(scope) }),
      moneyMoves: true,
      money: t('approveMoney'),
      approver: t('approveApprover'),
      ifNobody: t('approveIfNobody'),
      // the BFF step-up gates this route, so the code prompt must say what it authorises
      stepUpReason: t('approveStepUp', { scope: scopeName(scope) }),
      confirmLabel: t('approveResume'),
      fields: [],
      submit: () => approveLaneResume(scope),
      doneMessage: t('done.approve'),
    });

  return (
    <Stack spacing={1} sx={{ mt: 3 }} data-testid="lane-pauses">
      {queue.error && (
        <Alert severity="error" data-testid="lane-pause-error">
          {t('readFailed', { error: queue.error })}
        </Alert>
      )}
      {paused.map((state) => (
        <Alert
          key={state.scope}
          severity="warning"
          data-testid={`lane-pause-${state.scope}`}
          action={
            <PermissionButton
              resource={SIGNERS_PERMISSION_RESOURCE}
              action={state.resumeProposedBy ? 'approve' : 'propose'}
            >
              <Button
                color="inherit"
                size="small"
                sx={OUTCOME_BUTTON_SX}
                onClick={() => (state.resumeProposedBy ? approve : propose)(state.scope)}
              >
                {t(state.resumeProposedBy ? 'approveResume' : 'proposeResume')}
              </Button>
            </PermissionButton>
          }
        >
          <Typography variant="body2" component="div">
            {t('paused', { scope: scopeName(state.scope) })} <TimeText iso={state.at} />
          </Typography>
          {state.resumeProposedBy && (
            <Typography variant="body2" component="div">
              {t('resumeWaiting', { name: middle(state.resumeProposedBy) })}
            </Typography>
          )}
        </Alert>
      ))}
      <PermissionButton resource={SIGNERS_PERMISSION_RESOURCE} action="pause">
        <Button
          size="small"
          variant="outlined"
          color="warning"
          sx={{ ...OUTCOME_BUTTON_SX, alignSelf: 'flex-start' }}
          onClick={pause}
        >
          {t('pause')}
        </Button>
      </PermissionButton>
      {spec && <ConfirmDialog spec={spec} onClose={() => setSpec(null)} onSettled={onSettled} />}
    </Stack>
  );
};
